import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useOfflineSync } from '../contexts/OfflineSyncContext';
import { useTranslation } from 'react-i18next';

const ThermometerContainer = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const ThermometerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ThermometerTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ThermometerSubtitle = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.5rem 0 0 0;
`;

const ThermometerVisual = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  position: relative;
`;

const ThermometerScale = styled.div`
  width: 60px;
  height: 300px;
  background: linear-gradient(to top, 
    #10b981 0%, 
    #10b981 20%,
    #60a5fa 20%,
    #60a5fa 50%,
    #f59e0b 50%,
    #f59e0b 70%,
    #ef4444 70%,
    #ef4444 100%
  );
  border-radius: 30px 30px 0 0;
  position: relative;
  border: 3px solid #e5e7eb;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 50px;
    height: 250px;
  }
`;

const ThermometerFill = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: ${props => props.fillPercentage}%;
  background: ${props => {
    if (props.score >= 8) return '#ef4444';
    if (props.score >= 4) return '#f59e0b';
    if (props.score >= 1) return '#60a5fa';
    return '#10b981';
  }};
  border-radius: 30px 30px 0 0;
  transition: height 0.3s ease, background 0.3s ease;
  z-index: 1;
`;

const ScaleLabels = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 300px;
  margin-left: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  
  @media (max-width: 768px) {
    height: 250px;
    font-size: 0.75rem;
    margin-left: 0.5rem;
  }
`;

const SliderContainer = styled.div`
  width: 100%;
  margin: 2rem 0;
  position: relative;
  padding: 1rem 0;
  
  &:hover {
    cursor: pointer;
  }
`;

const SliderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  position: relative;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const DecrementButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  user-select: none;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #d1d5db;
    transform: scale(1.1);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
  }
`;

const IncrementButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  user-select: none;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #d1d5db;
    transform: scale(1.1);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
  }
`;

const Slider = styled.input`
  flex: 1;
  height: 16px;
  border-radius: 8px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    height: 18px;
  }
  
  &:active {
    cursor: grabbing;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${props => {
      if (props.value >= 8) return '#ef4444';
      if (props.value >= 4) return '#f59e0b';
      if (props.value >= 1) return '#60a5fa';
      return '#10b981';
    }};
    cursor: grab;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.8);
    transition: all 0.2s ease;
    border: 3px solid white;
    position: relative;
  }
  
  &::-webkit-slider-thumb::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
  }
  
  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4), 0 0 0 6px rgba(255, 255, 255, 0.9);
  }
  
  &::-webkit-slider-thumb:active {
    cursor: grabbing;
    transform: scale(1.15);
  }
  
  &::-moz-range-thumb {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${props => {
      if (props.value >= 8) return '#ef4444';
      if (props.value >= 4) return '#f59e0b';
      if (props.value >= 1) return '#60a5fa';
      return '#10b981';
    }};
    cursor: grab;
    border: 3px solid white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.8);
    transition: all 0.2s ease;
  }
  
  &::-moz-range-thumb:hover {
    transform: scale(1.2);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.4), 0 0 0 6px rgba(255, 255, 255, 0.9);
  }
  
  &::-moz-range-thumb:active {
    cursor: grabbing;
    transform: scale(1.15);
  }
  
  &::-webkit-slider-runnable-track {
    height: 16px;
    border-radius: 8px;
    background: linear-gradient(to right,
      #10b981 0%,
      #10b981 10%,
      #60a5fa 10%,
      #60a5fa 40%,
      #f59e0b 40%,
      #f59e0b 70%,
      #ef4444 70%,
      #ef4444 100%
    );
    transition: height 0.2s ease;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &::-moz-range-track {
    height: 16px;
    border-radius: 8px;
    background: linear-gradient(to right,
      #10b981 0%,
      #10b981 10%,
      #60a5fa 10%,
      #60a5fa 40%,
      #f59e0b 40%,
      #f59e0b 70%,
      #ef4444 70%,
      #ef4444 100%
    );
    transition: height 0.2s ease;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:focus {
    outline: none;
    
    &::-webkit-slider-thumb {
      box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3);
    }
    
    &::-moz-range-thumb {
      box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3);
    }
  }
`;

const ScaleNumbers = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 600;
`;

const ScoreDisplay = styled.div`
  text-align: center;
  margin: 1rem 0;
  font-size: 2rem;
  font-weight: 700;
  color: ${props => {
    if (props.score >= 8) return '#ef4444';
    if (props.score >= 4) return '#f59e0b';
    if (props.score >= 1) return '#60a5fa';
    return '#10b981';
  }};
  transition: color 0.3s ease;
`;

const ScoreLabel = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.5rem;
`;

const FormSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e5e7eb;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: #0B63C5;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #094F9E;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ClearButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

// NCCN-aligned problem list (National Comprehensive Cancer Network guidelines)
// These are the keys - actual translations come from i18n
const distressSourceKeys = [
  // Practical Problems
  'transportation',
  'housing',
  'insuranceFinancial',
  'workSchool',
  // Family Problems
  'dealingWithPartner',
  'dealingWithChildren',
  'familyHealthIssues',
  // Emotional Problems
  'depression',
  'fears',
  'nervousness',
  'sadness',
  'worry',
  'lossOfInterest',
  // Spiritual/Religious Concerns
  'spiritualReligiousConcerns',
  // Physical Problems
  'fatigue',
  'pain',
  'sleepProblems',
  'eatingProblems',
  'bathingDressing',
  'breathingProblems',
  'nausea',
  'memoryConcentration',
  'mouthSores',
  'skinProblems',
  'tinglingHandsFeet',
  'otherPhysicalProblems'
];

// Emoji mapping for distress scores
const getDistressEmoji = (score) => {
  if (score >= 8) return '😰'; // Extreme distress - very anxious face
  if (score >= 4) return '😟'; // Moderate to severe - worried face
  if (score >= 1) return '😐'; // Mild - neutral face
  return '😊'; // None - happy face
};

// Emoji mapping for problem list items
const problemEmojis = {
  // Practical Problems
  transportation: '🚗',
  housing: '🏠',
  insuranceFinancial: '💰',
  workSchool: '💼',
  // Family Problems
  dealingWithPartner: '👫',
  dealingWithChildren: '👨‍👩‍👧',
  familyHealthIssues: '👪',
  // Emotional Problems
  depression: '😔',
  fears: '😨',
  nervousness: '😰',
  sadness: '😢',
  worry: '😟',
  lossOfInterest: '😑',
  // Spiritual/Religious Concerns
  spiritualReligiousConcerns: '🙏',
  // Physical Problems
  fatigue: '😴',
  pain: '🤕',
  sleepProblems: '😴',
  eatingProblems: '🍽️',
  bathingDressing: '🛁',
  breathingProblems: '😮‍💨',
  nausea: '🤢',
  memoryConcentration: '🧠',
  mouthSores: '👄',
  skinProblems: '🤲',
  tinglingHandsFeet: '🦶',
  otherPhysicalProblems: '🏥'
};

const DistressThermometer = ({ onSubmitted }) => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const { isOnline, queueDistressLog } = useOfflineSync();
  const [distressScore, setDistressScore] = useState(0);
  const [distressNotes, setDistressNotes] = useState('');
  const [selectedSources, setSelectedSources] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [numberInputStr, setNumberInputStr] = useState('0');
  const numberInputFocusedRef = useRef(false);
  const sliderRef = useRef(null);
  const incrementIntervalRef = useRef(null);
  const decrementIntervalRef = useRef(null);

  // Keep number input string in sync with score when not focused (e.g. slider / +/-)
  useEffect(() => {
    if (!numberInputFocusedRef.current) {
      setNumberInputStr(String(distressScore));
    }
  }, [distressScore]);

  const handleScoreChange = (e) => {
    setDistressScore(parseInt(e.target.value));
  };

  // Add mouse wheel support for scrolling the slider
  const handleWheel = useCallback((e) => {
    if (sliderRef.current && document.activeElement === sliderRef.current) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1 : -1;
      const newScore = Math.max(0, Math.min(10, distressScore + delta));
      setDistressScore(newScore);
      
      // Update the slider value
      if (sliderRef.current) {
        sliderRef.current.value = newScore;
      }
    }
  }, [distressScore]);

  // Add keyboard navigation support
  const handleKeyDown = useCallback((e) => {
    if (sliderRef.current && document.activeElement === sliderRef.current) {
      let newScore = distressScore;
      
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
        e.preventDefault();
        newScore = Math.min(10, distressScore + 1);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
        e.preventDefault();
        newScore = Math.max(0, distressScore - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        newScore = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        newScore = 10;
      }
      
      if (newScore !== distressScore) {
        setDistressScore(newScore);
        if (sliderRef.current) {
          sliderRef.current.value = newScore;
        }
      }
    }
  }, [distressScore]);

  // Add click-to-jump functionality on the track
  const handleSliderContainerClick = useCallback((e) => {
    if (sliderRef.current && e.target !== sliderRef.current) {
      const rect = sliderRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newScore = Math.round(percentage * 10);
      const clampedScore = Math.max(0, Math.min(10, newScore));
      setDistressScore(clampedScore);
      if (sliderRef.current) {
        sliderRef.current.value = clampedScore;
      }
    }
  }, []);

  // Attach event listeners
  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('wheel', handleWheel, { passive: false });
      slider.addEventListener('keydown', handleKeyDown);
      
      return () => {
        slider.removeEventListener('wheel', handleWheel);
        slider.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [handleWheel, handleKeyDown]);

  const handleSourceToggle = (source) => {
    setSelectedSources(prev => 
      prev.includes(source) 
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !token) {
      toast.error(t('distressThermometer.errors.login'));
      return;
    }

    setSubmitting(true);

    // Store keys - translation will happen on display
    const payload = {
      distressScore,
      distressNotes: distressNotes.trim() || undefined,
      distressSources: selectedSources.length > 0 ? selectedSources : undefined, // Store keys, not translated strings
      timestamp: new Date().toISOString()
    };

    const resetForm = () => {
      setDistressScore(0);
      setNumberInputStr('0');
      setDistressNotes('');
      setSelectedSources([]);
    };

    if (!isOnline) {
      queueDistressLog(payload);
      resetForm();
      if (onSubmitted) {
        onSubmitted();
      }
      setSubmitting(false);
      return;
    }

    try {
      await axios.post('/api/distress-logs', payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success(t('distressThermometer.success.recorded', { score: distressScore }));
      
      resetForm();
      
      if (onSubmitted) {
        onSubmitted();
      }
    } catch (error) {
      console.error('Error submitting distress:', error);

      if (!error.response || error.code === 'ERR_NETWORK') {
        queueDistressLog(payload);
        resetForm();
        if (onSubmitted) {
          onSubmitted();
        }
      } else {
        toast.error(error.response?.data?.message || t('distressThermometer.errors.recordFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    setDistressScore(0);
    setNumberInputStr('0');
    setDistressNotes('');
    setSelectedSources([]);
  };

  // Handle increment with long-press support
  const handleIncrement = useCallback(() => {
    setDistressScore(prev => {
      const newScore = Math.min(10, prev + 1);
      if (sliderRef.current) {
        sliderRef.current.value = newScore;
      }
      return newScore;
    });
  }, []);

  // Handle decrement with long-press support
  const handleDecrement = useCallback(() => {
    setDistressScore(prev => {
      const newScore = Math.max(0, prev - 1);
      if (sliderRef.current) {
        sliderRef.current.value = newScore;
      }
      return newScore;
    });
  }, []);

  const incrementTimeoutRef = useRef(null);
  const decrementTimeoutRef = useRef(null);
  const lastActionTimeRef = useRef({ increment: 0, decrement: 0 });
  const isProcessingRef = useRef({ increment: false, decrement: false });
  const touchStartedRef = useRef({ increment: false, decrement: false });

  // Start continuous increment
  const startIncrement = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double triggers on mobile (touch + mouse events)
    const now = Date.now();
    const timeSinceLastAction = now - lastActionTimeRef.current.increment;
    
    // If already processing or called too recently, ignore
    if (isProcessingRef.current.increment || timeSinceLastAction < 500) {
      return;
    }
    
    isProcessingRef.current.increment = true;
    lastActionTimeRef.current.increment = now;
    
    handleIncrement();
    
    // Clear any existing timeout
    if (incrementTimeoutRef.current) {
      clearTimeout(incrementTimeoutRef.current);
    }
    // Start continuous increment after 500ms
    incrementTimeoutRef.current = setTimeout(() => {
      incrementIntervalRef.current = setInterval(() => {
        setDistressScore(prev => {
          if (prev >= 10) {
            if (incrementIntervalRef.current) {
              clearInterval(incrementIntervalRef.current);
              incrementIntervalRef.current = null;
            }
            return prev;
          }
          const newScore = Math.min(10, prev + 1);
          if (sliderRef.current) {
            sliderRef.current.value = newScore;
          }
          return newScore;
        });
      }, 150);
    }, 500);
    
    // Reset processing flag after a delay
    setTimeout(() => {
      isProcessingRef.current.increment = false;
    }, 500);
  }, [handleIncrement]);

  // Stop continuous increment
  const stopIncrement = useCallback(() => {
    isProcessingRef.current.increment = false;
    if (incrementTimeoutRef.current) {
      clearTimeout(incrementTimeoutRef.current);
      incrementTimeoutRef.current = null;
    }
    if (incrementIntervalRef.current) {
      clearInterval(incrementIntervalRef.current);
      incrementIntervalRef.current = null;
    }
  }, []);

  // Start continuous decrement
  const startDecrement = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double triggers on mobile (touch + mouse events)
    const now = Date.now();
    const timeSinceLastAction = now - lastActionTimeRef.current.decrement;
    
    // If already processing or called too recently, ignore
    if (isProcessingRef.current.decrement || timeSinceLastAction < 500) {
      return;
    }
    
    isProcessingRef.current.decrement = true;
    lastActionTimeRef.current.decrement = now;
    
    handleDecrement();
    
    // Clear any existing timeout
    if (decrementTimeoutRef.current) {
      clearTimeout(decrementTimeoutRef.current);
    }
    // Start continuous decrement after 500ms
    decrementTimeoutRef.current = setTimeout(() => {
      decrementIntervalRef.current = setInterval(() => {
        setDistressScore(prev => {
          if (prev <= 0) {
            if (decrementIntervalRef.current) {
              clearInterval(decrementIntervalRef.current);
              decrementIntervalRef.current = null;
            }
            return prev;
          }
          const newScore = Math.max(0, prev - 1);
          if (sliderRef.current) {
            sliderRef.current.value = newScore;
          }
          return newScore;
        });
      }, 150);
    }, 500);
    
    // Reset processing flag after a delay
    setTimeout(() => {
      isProcessingRef.current.decrement = false;
    }, 500);
  }, [handleDecrement]);

  // Stop continuous decrement
  const stopDecrement = useCallback(() => {
    isProcessingRef.current.decrement = false;
    if (decrementTimeoutRef.current) {
      clearTimeout(decrementTimeoutRef.current);
      decrementTimeoutRef.current = null;
    }
    if (decrementIntervalRef.current) {
      clearInterval(decrementIntervalRef.current);
      decrementIntervalRef.current = null;
    }
  }, []);

  // Cleanup intervals and timeouts on unmount
  useEffect(() => {
    return () => {
      stopIncrement();
      stopDecrement();
      if (incrementTimeoutRef.current) {
        clearTimeout(incrementTimeoutRef.current);
      }
      if (decrementTimeoutRef.current) {
        clearTimeout(decrementTimeoutRef.current);
      }
    };
  }, [stopIncrement, stopDecrement]);

  const fillPercentage = (distressScore / 10) * 100;
  const scoreLabel = distressScore === 0 ? t('distress.labels.none') :
                     distressScore <= 3 ? t('distress.labels.mild') :
                     distressScore <= 6 ? t('distress.labels.moderateToSevere') :
                     distressScore <= 8 ? t('distress.labels.moderateToSevere') :
                     t('distress.labels.extreme');
  
  // NCCN threshold: scores of 4 or higher indicate clinically significant distress
  const isClinicallySignificant = distressScore >= 4;

  return (
    <ThermometerContainer>
      <ThermometerHeader>
        <div>
          <ThermometerTitle>
            {t('distressThermometer.title')}
          </ThermometerTitle>
          <ThermometerSubtitle>
            {t('distressThermometer.subtitle')}
            <br />
            <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>{t('distressThermometer.guidelines')}</span>
          </ThermometerSubtitle>
        </div>
      </ThermometerHeader>

      <form onSubmit={handleSubmit}>
        <ThermometerVisual>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem' }}>
            <ThermometerScale>
              <ThermometerFill 
                fillPercentage={fillPercentage} 
                score={distressScore}
              />
            </ThermometerScale>
            <ScaleLabels>
              <span>10</span>
              <span>8</span>
              <span>6</span>
              <span>4</span>
              <span>2</span>
              <span>0</span>
            </ScaleLabels>
          </div>
          
          <ScoreDisplay score={distressScore}>
            <span style={{ fontSize: '2.5rem', marginRight: '0.5rem' }}>
              {getDistressEmoji(distressScore)}
            </span>
            {distressScore}
          </ScoreDisplay>
          <ScoreLabel>{scoreLabel}</ScoreLabel>
          {isClinicallySignificant && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.75rem 1rem',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '2px solid #f59e0b',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              color: '#92400e',
              fontWeight: 600,
              textAlign: 'center'
            }}>
              {t('distressThermometer.alert', { score: distressScore })}
            </div>
          )}
        </ThermometerVisual>

        <SliderContainer onClick={handleSliderContainerClick}>
          <SliderWrapper>
            <DecrementButton
              type="button"
              onMouseDown={(e) => {
                // On mobile, mouse events fire after touch events - ignore if touch already handled
                if (touchStartedRef.current.decrement) {
                  touchStartedRef.current.decrement = false;
                  return;
                }
                if (!('ontouchstart' in window)) {
                  startDecrement(e);
                }
              }}
              onMouseUp={stopDecrement}
              onMouseLeave={stopDecrement}
              onTouchStart={(e) => {
                e.preventDefault();
                touchStartedRef.current.decrement = true;
                startDecrement(e);
              }}
              onTouchEnd={(e) => {
                touchStartedRef.current.decrement = false;
                stopDecrement();
              }}
              onTouchCancel={(e) => {
                touchStartedRef.current.decrement = false;
                stopDecrement();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled={distressScore === 0}
              aria-label={t('distressThermometer.decrease', { defaultValue: 'Decrease distress score' })}
            >
              −
            </DecrementButton>
            
          <Slider
            ref={sliderRef}
            type="range"
            min="0"
            max="10"
            step="1"
            value={distressScore}
            onChange={handleScoreChange}
            aria-label={t('distressThermometer.sliderLabel', { defaultValue: 'Distress level slider' })}
            aria-valuemin="0"
            aria-valuemax="10"
            aria-valuenow={distressScore}
            tabIndex={0}
          />
            
            <IncrementButton
              type="button"
              onMouseDown={(e) => {
                // On mobile, mouse events fire after touch events - ignore if touch already handled
                if (touchStartedRef.current.increment) {
                  touchStartedRef.current.increment = false;
                  return;
                }
                if (!('ontouchstart' in window)) {
                  startIncrement(e);
                }
              }}
              onMouseUp={stopIncrement}
              onMouseLeave={stopIncrement}
              onTouchStart={(e) => {
                e.preventDefault();
                touchStartedRef.current.increment = true;
                startIncrement(e);
              }}
              onTouchEnd={(e) => {
                touchStartedRef.current.increment = false;
                stopIncrement();
              }}
              onTouchCancel={(e) => {
                touchStartedRef.current.increment = false;
                stopIncrement();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled={distressScore === 10}
              aria-label={t('distressThermometer.increase', { defaultValue: 'Increase distress score' })}
            >
              +
            </IncrementButton>
          </SliderWrapper>
          <ScaleNumbers>
            <span>0</span>
            <span>2</span>
            <span>4</span>
            <span>6</span>
            <span>8</span>
            <span>10</span>
          </ScaleNumbers>
        </SliderContainer>
        
        {/* Direct number input for precise selection */}
        <div style={{ 
          marginTop: '1rem', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '0.5rem'
        }}>
          <label htmlFor="distress-number-input" style={{ 
            fontSize: '0.875rem', 
            fontWeight: 500, 
            color: '#6b7280' 
          }}>
            {t('distressThermometer.notes.enterNumberHint')}
          </label>
          <input
            id="distress-number-input"
            type="number"
            min="0"
            max="10"
            step="1"
            value={numberInputStr}
            onFocus={() => { numberInputFocusedRef.current = true; }}
            onBlur={() => {
              numberInputFocusedRef.current = false;
              const parsed = parseInt(numberInputStr, 10);
              const clamped = Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(10, parsed));
              setDistressScore(clamped);
              setNumberInputStr(String(clamped));
              if (sliderRef.current) {
                sliderRef.current.value = clamped;
              }
            }}
            onChange={(e) => {
              const raw = e.target.value;
              setNumberInputStr(raw);
              const val = parseInt(raw, 10);
              if (!Number.isNaN(val) && val >= 0 && val <= 10) {
                setDistressScore(val);
                if (sliderRef.current) {
                  sliderRef.current.value = val;
                }
              }
            }}
            onWheel={(e) => {
              // Prevent scroll from changing number input value
              e.target.blur();
            }}
            style={{
              width: '60px',
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              textAlign: 'center',
              fontWeight: 600,
              color: '#374151'
            }}
          />
        </div>

        <FormSection>
          <FormLabel>
            {t('distressThermometer.problemList.label')}
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem' }}>
              {t('distressThermometer.problemList.helper')}
            </span>
          </FormLabel>
          <CheckboxGroup>
            {distressSourceKeys.map(sourceKey => {
              const translatedSource = t(`distressThermometer.sources.${sourceKey}`);
              const emoji = problemEmojis[sourceKey] || '📋';
              return (
                <CheckboxLabel key={sourceKey}>
                  <input
                    type="checkbox"
                    checked={selectedSources.includes(sourceKey)}
                    onChange={() => handleSourceToggle(sourceKey)}
                  />
                  <span>
                    <span style={{ marginRight: '0.5rem', fontSize: '1.1rem' }}>{emoji}</span>
                    {translatedSource}
                  </span>
                </CheckboxLabel>
              );
            })}
          </CheckboxGroup>
        </FormSection>

        <FormSection>
          <FormLabel>{t('distressThermometer.notes.label')}</FormLabel>
          <TextArea
            placeholder={t('distressThermometer.notes.placeholder')}
            value={distressNotes}
            onChange={(e) => setDistressNotes(e.target.value)}
            maxLength={1000}
          />
        </FormSection>

        <ButtonGroup>
          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? t('distressThermometer.buttons.recording') : t('distressThermometer.buttons.record')}
          </SubmitButton>
          <ClearButton type="button" onClick={handleClear}>
            {t('distressThermometer.buttons.clear')}
          </ClearButton>
        </ButtonGroup>
      </form>
    </ThermometerContainer>
  );
};

export default DistressThermometer;

